/* eslint-disable jsx-a11y/aria-role */
/*
 * Exception for ARIA role="text" being added to axe-core in 4.2, this will currently display as an error in an automated test using either Axe or Lighthouse
 * https://github.com/dequelabs/axe-core/milestone/19
 * https://github.com/dequelabs/axe-core/issues/1597
 */

import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { useIntl } from 'gatsby-plugin-react-intl';

import RichText from '../../../Core/RichText';
import Link from '../../../Core/Link';

import {
  Points,
  ContentWrapper,
  ImageWrapper,
  Heading,
  HeaderImage,
  ImagePoints,
  Body,
  SmallCopy,
  ColumnHeading,
  PointValue,
  VendorSetContainer,
  VendorImages,
} from './styles';

const ColumnPoints = ({
  dataItem = null,
  componentStyle = null,
  className = '',
  columnCount = '',
  isNotActive = '',
  index = 0,
  percentage = false,
}) => {
  // * Null check for data
  if (!dataItem) return null;
  const intl = useIntl();

  // * Destructure data values
  const {
    id,
    selectable,
    colspan,
    heading,
    bodyCopy,
    imageDam,
    link,
    pointValue,
    valueSubtext,
    vendorImageDam,
    vendorImageSetsDam,
    backgroundStyle,
  } = dataItem;

  let scope = null;
  if (index === 0 && componentStyle !== 'product-page-points-column') {
    scope = 'row';
  } else {
    scope = null;
  }

  return (
    <Points
      key={id}
      className={`${className} ${componentStyle} ${backgroundStyle} ${isNotActive}`}
      columnCount={columnCount}
      as={(heading || imageDam) && index === 0 && !pointValue ? 'th' : null}
      scope={(heading || imageDam) && index === 0 ? scope : null}
      colSpan={colspan}
      role="cell"
      selectable={selectable}
    >
      {vendorImageDam && (
        <ContentWrapper className={`${componentStyle} ${className}`}>
          <ImageWrapper className={componentStyle}>
            <HeaderImage src={vendorImageDam} alt={vendorImageDam.altText} />
          </ImageWrapper>
        </ContentWrapper>
      )}
      {valueSubtext && imageDam && pointValue && (
        <ContentWrapper className={`${componentStyle} ${className}`}>
          <Body className={componentStyle} role="text">
            {bodyCopy && (
              <ColumnHeading className={componentStyle}>
                <RichText document={bodyCopy} />
              </ColumnHeading>
            )}
            <PointValue
              aria-label={pointValue === '-' ? `${valueSubtext}` : null}
            >
              {percentage
                ? `${parseInt(pointValue, 10) / 10}${
                    intl.locale === 'en' ? '%' : ' %'
                  }`
                : pointValue}
            </PointValue>
            <ImagePoints
              src={imageDam}
              alt={imageDam.altText}
              percentage={percentage}
            />
            <SmallCopy columnCount={columnCount}>
              {ReactHtmlParser(valueSubtext)}
            </SmallCopy>
          </Body>
        </ContentWrapper>
      )}
      {!valueSubtext && !imageDam && pointValue && (
        <ContentWrapper className={`${componentStyle} ${className}`}>
          <Body className={componentStyle} role="text">
            <PointValue>{pointValue}</PointValue>
          </Body>
        </ContentWrapper>
      )}
      {valueSubtext && !imageDam && pointValue && (
        <ContentWrapper className={`${componentStyle} ${className}`}>
          <Body className={componentStyle} role="text">
            <PointValue
              aria-label={pointValue === '-' ? `${valueSubtext}` : null}
            >
              {pointValue}
            </PointValue>
          </Body>
        </ContentWrapper>
      )}
      {!heading && !valueSubtext && !imageDam && !pointValue && bodyCopy && (
        <ContentWrapper className={`${componentStyle} ${className}`}>
          <Body className={componentStyle} role="text">
            {bodyCopy && (
              <ColumnHeading className={componentStyle}>
                <RichText document={bodyCopy} />
              </ColumnHeading>
            )}
          </Body>
        </ContentWrapper>
      )}
      {index === 0 && imageDam && !pointValue && (
        <ContentWrapper className={`${componentStyle} ${className}`}>
          <ImageWrapper className={componentStyle}>
            {imageDam && link ? (
              <Link className="unstyled-link" to={`${link}`}>
                <HeaderImage src={imageDam} alt={imageDam.altText} />
              </Link>
            ) : (
              <HeaderImage src={imageDam} alt={imageDam.altText} />
            )}
          </ImageWrapper>
        </ContentWrapper>
      )}
      {index === 0 && heading && !pointValue && (
        <ContentWrapper className={`${componentStyle} ${className}`}>
          <Heading className={componentStyle}>
            <RichText document={heading} />
            {vendorImageSetsDam && (
              <VendorSetContainer>
                {vendorImageSetsDam.map(img => (
                  <VendorImages key={img.id} src={img} alt={img.altText} />
                ))}
                {bodyCopy && (
                  <ColumnHeading className={`${componentStyle} vendor-row`}>
                    <RichText document={bodyCopy} />
                  </ColumnHeading>
                )}
              </VendorSetContainer>
            )}
          </Heading>
        </ContentWrapper>
      )}
    </Points>
  );
};

ColumnPoints.propTypes = {
  dataItem: PropTypes.shape(),
  componentStyle: PropTypes.string,
  className: PropTypes.string,
  columnCount: PropTypes.string,
  index: PropTypes.number,
  isNotActive: PropTypes.string,
  percentage: PropTypes.bool,
};

export default ColumnPoints;
